import { Button } from "@/components/ui/button";
import { SleepStepContainer } from "../SleepStepContainer";
import { useSleepForm } from "../SleepFormContext";
import { CheckCircle2, XCircle, HelpCircle } from "lucide-react";

interface WakeStatusStepProps {
  childName?: string;
  onNext: () => void;
}

export function WakeStatusStep({ childName, onNext }: WakeStatusStepProps) {
  const form = useSleepForm();
  const currentValue = form.watch("has_woken_up");

  const handleSelect = (value: 'yes' | 'no' | 'unknown') => {
    form.setValue("has_woken_up", value, { shouldValidate: true });
    
    // If status changes from yes to no/unknown, clear end time and restfulness
    if (value !== 'yes') {
      form.setValue("end_time", undefined, { shouldValidate: true });
      form.setValue("end_time_input", "", { shouldValidate: true });
      form.setValue("restfulness_id", "", { shouldValidate: true });
      form.setValue("status", "in_progress", { shouldValidate: true });
    } else {
      form.setValue("status", "complete", { shouldValidate: true });
    }
    
    // Auto-advance after selection
    setTimeout(() => {
      onNext();
    }, 300);
  };

  const options = [
    {
      value: 'yes' as const,
      label: 'Yes',
      icon: CheckCircle2,
      description: "Baby is awake"
    },
    {
      value: 'no' as const,
      label: 'No',
      icon: XCircle,
      description: "Still sleeping"
    },
    {
      value: 'unknown' as const,
      label: 'Unknown',
      icon: HelpCircle,
      description: "Not sure yet"
    }
  ];

  return (
    <SleepStepContainer>
      <div className="space-y-4">
        <div className="space-y-3">
          {options.map((option) => {
            const Icon = option.icon;
            const isSelected = currentValue === option.value;
            
            return (
              <Button
                key={option.value}
                type="button"
                variant={isSelected ? "default" : "outline"}
                onClick={() => handleSelect(option.value)}
                className={`w-full h-auto py-4 px-4 flex items-center justify-between ${
                  isSelected 
                    ? 'bg-[#9b87f5] hover:bg-[#9b87f5]/90 text-white border-[#9b87f5]' 
                    : 'border-[#333333] bg-[#222222] hover:bg-[#333333] hover:text-[#D6BCFA]'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${isSelected ? 'text-white' : 'text-[#9b87f5]'}`} />
                  <div className="text-left">
                    <div className="font-semibold">{option.label}</div>
                    <div className={`text-sm ${isSelected ? 'text-white/80' : 'text-[#8E9196]'}`}>
                      {option.description}
                    </div>
                  </div>
                </div>
                {isSelected && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </Button>
            );
          })}
        </div>
      </div>
    </SleepStepContainer>
  );
}
